import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Users, Plus, Pencil, Trash2, ChevronRight,
  Shield, UserPlus, ArrowLeft, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('veloflux_token')}` });

// All modules that can be configured per company
const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'bicycles', label: 'Bicicletas' },
  { key: 'models', label: 'Modelos' },
  { key: 'tallas', label: 'Tallas' },
  { key: 'inventory', label: 'Inventario' },
  { key: 'internal-orders', label: 'Pedidos Internos' },
  { key: 'messages', label: 'Mensajes' },
  { key: 'showroom', label: '2ª Mano' },
  { key: 'accesorios', label: 'Accesorios' },
  { key: 'nombre-labels', label: 'Nombres' },
  { key: 'pending-payments', label: 'Pendientes Cobro' },
  { key: 'commissions', label: 'Comisiones' },
  { key: 'daily-cash', label: 'Caja Diaria' },
  { key: 'calendario', label: 'Calendario' },
  { key: 'sold', label: 'Vendidas' },
  { key: 'materials', label: 'Materiales' },
  { key: 'general-expenses', label: 'Gastos Generales' },
  { key: 'users', label: 'Usuarios' },
];

// ========== Empresa Detail with Module Config ==========
function EmpresaDetail({ empresa, users, onBack, onUpdate }) {
  const [visibleModules, setVisibleModules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize from empresa data
    setVisibleModules(empresa.visible_modules || null);
  }, [empresa]);

  const allEnabled = !visibleModules; // null means all enabled

  const toggleModule = (key) => {
    if (allEnabled) {
      // Switch from "all enabled" to specific selection (minus this one)
      setVisibleModules(ALL_MODULES.filter(m => m.key !== key).map(m => m.key));
    } else {
      if (visibleModules.includes(key)) {
        setVisibleModules(visibleModules.filter(k => k !== key));
      } else {
        setVisibleModules([...visibleModules, key]);
      }
    }
  };

  const enableAll = () => setVisibleModules(null);

  const saveModules = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/empresas/${empresa.id}/modules`, { visible_modules: visibleModules }, { headers: headers() });
      toast.success('Módulos actualizados');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const isEnabled = (key) => allEnabled || (visibleModules && visibleModules.includes(key));

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-to-empresas">
        <ArrowLeft size={16} /> Volver a empresas
      </button>
      <div className="flex items-center gap-3 mb-6">
        <Building2 size={20} className="text-indigo-600" />
        <h3 className="text-lg font-semibold text-slate-800">{empresa.nombre}</h3>
        <span className="text-sm text-slate-500">{users.length} usuario(s)</span>
      </div>

      {/* Users */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-slate-600 mb-2">Usuarios</h4>
        {users.length === 0 ? (
          <p className="text-slate-500 text-sm">No hay usuarios en esta empresa.</p>
        ) : (
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200" data-testid={`empresa-user-${u.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{u.name}</p>
                  <p className="text-sm text-slate-500">{u.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                  u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : u.role_name || 'Mecánico'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Module Configuration */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-600">Módulos visibles en sidebar</h4>
          <div className="flex gap-2">
            {!allEnabled && (
              <Button size="sm" variant="outline" onClick={enableAll} className="text-xs">
                Habilitar todos
              </Button>
            )}
            <Button size="sm" onClick={saveModules} disabled={saving} className="text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="save-modules-btn">
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
        {allEnabled && (
          <p className="text-xs text-green-600 mb-3">Todos los módulos están habilitados. Desactiva los que no necesite esta empresa.</p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ALL_MODULES.map(mod => (
            <div key={mod.key} className={`flex items-center justify-between p-2 rounded-lg border ${isEnabled(mod.key) ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}
              data-testid={`module-toggle-${mod.key}`}>
              <span className={`text-sm ${isEnabled(mod.key) ? 'text-slate-800' : 'text-slate-400'}`}>{mod.label}</span>
              <Switch checked={isEnabled(mod.key)} onCheckedChange={() => toggleModule(mod.key)} />
            </div>
          ))}
        </div>
        {visibleModules && (
          <p className="text-xs text-slate-400 mt-2">
            {visibleModules.length} de {ALL_MODULES.length} módulos habilitados
          </p>
        )}
      </div>
    </div>
  );
}

// ========== Empresas Tab ==========
function EmpresasTab() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [colorPrimario, setColorPrimario] = useState('orange');
  const [createModules, setCreateModules] = useState(ALL_MODULES.map(m => m.key));
  const [deleting, setDeleting] = useState(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [empresaUsers, setEmpresaUsers] = useState([]);

  const fetchEmpresas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empresas`, { headers: headers() });
      setEmpresas(res.data);
    } catch { toast.error('Error al cargar empresas'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);

  const fetchEmpresaUsers = async (emp) => {
    try {
      const res = await axios.get(`${API}/empresas/${emp.id}/users`, { headers: headers() });
      setEmpresaUsers(res.data);
      setSelectedEmpresa(emp);
    } catch { toast.error('Error al cargar usuarios'); }
  };

  const COLORS = [
    { name: 'orange', label: 'Naranja', hex: '#f97316' },
    { name: 'blue', label: 'Azul', hex: '#3b82f6' },
    { name: 'green', label: 'Verde', hex: '#22c55e' },
    { name: 'red', label: 'Rojo', hex: '#ef4444' },
    { name: 'violet', label: 'Violeta', hex: '#8b5cf6' },
    { name: 'teal', label: 'Teal', hex: '#14b8a6' },
    { name: 'amber', label: 'Ámbar', hex: '#f59e0b' },
    { name: 'slate', label: 'Gris', hex: '#64748b' },
  ];

  const handleSave = async () => {
    if (!nombre.trim()) return toast.error('El nombre es obligatorio');
    try {
      const payload = { nombre: nombre.trim(), color_primario: colorPrimario };
      if (editing) {
        await axios.put(`${API}/empresas/${editing.id}`, payload, { headers: headers() });
        toast.success('Empresa actualizada');
      } else {
        // For new companies, send selected modules. If all selected -> null (all visible).
        const allSelected = createModules.length === ALL_MODULES.length;
        payload.visible_modules = allSelected ? null : createModules;
        await axios.post(`${API}/empresas`, payload, { headers: headers() });
        toast.success('Empresa creada');
      }
      setShowDialog(false);
      setEditing(null);
      setNombre('');
      setColorPrimario('orange');
      setCreateModules(ALL_MODULES.map(m => m.key));
      fetchEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const toggleCreateModule = (key) => {
    setCreateModules(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const setAllCreateModules = (enabled) => {
    setCreateModules(enabled ? ALL_MODULES.map(m => m.key) : []);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/empresas/${deleting.id}`, { headers: headers() });
      toast.success('Empresa eliminada');
      setDeleting(null);
      fetchEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (selectedEmpresa) {
    return (
      <EmpresaDetail 
        empresa={selectedEmpresa} 
        users={empresaUsers} 
        onBack={() => { setSelectedEmpresa(null); setEmpresaUsers([]); }}
        onUpdate={fetchEmpresas}
      />
    );
  }

  if (loading) return <p className="text-slate-500">Cargando empresas...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-700">Empresas registradas</h3>
        <Button
          size="sm"
          onClick={() => { setNombre(''); setColorPrimario('orange'); setCreateModules(ALL_MODULES.map(m => m.key)); setEditing(null); setShowDialog(true); }}
          data-testid="create-empresa-btn"
        >
          <Plus size={16} className="mr-1" /> Nueva Empresa
        </Button>
      </div>

      {empresas.length === 0 ? (
        <p className="text-slate-500 text-sm">No hay empresas registradas.</p>
      ) : (
        <div className="space-y-2">
          {empresas.map(emp => (
            <div key={emp.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`empresa-row-${emp.id}`}>
              <button
                onClick={() => fetchEmpresaUsers(emp)}
                className="flex items-center gap-3 text-left flex-1"
                data-testid={`empresa-detail-${emp.id}`}
              >
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: COLORS.find(c => c.name === emp.color_primario)?.hex || '#f97316' }}
                >
                  <Building2 size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{emp.nombre}</p>
                  <p className="text-xs text-slate-500">{emp.user_count} usuario(s)</p>
                </div>
                <ChevronRight size={16} className="text-slate-400 ml-auto" />
              </button>
              <div className="flex gap-1 ml-3">
                <button
                  onClick={() => { setNombre(emp.nombre); setColorPrimario(emp.color_primario || 'orange'); setEditing(emp); setShowDialog(true); }}
                  className="p-1.5 hover:bg-slate-100 rounded"
                  data-testid={`edit-empresa-${emp.id}`}
                >
                  <Pencil size={14} className="text-slate-500" />
                </button>
                <button
                  onClick={() => setDeleting(emp)}
                  className="p-1.5 hover:bg-red-50 rounded"
                  data-testid={`delete-empresa-${emp.id}`}
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Modifica los datos de la empresa.' : 'Crea una nueva empresa y selecciona los módulos visibles para sus usuarios.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre</Label>
              <Input
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                placeholder="Nombre de la empresa"
                data-testid="empresa-name-input"
                onKeyDown={e => e.key === 'Enter' && handleSave()}
              />
            </div>
            <div>
              <Label>Color principal</Label>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {COLORS.map(c => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setColorPrimario(c.name)}
                    className={`w-8 h-8 rounded-lg transition-all ${colorPrimario === c.name ? 'ring-2 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: c.hex, ringColor: c.hex }}
                    title={c.label}
                    data-testid={`color-${c.name}`}
                  />
                ))}
              </div>
            </div>
            {!editing && (
              <div data-testid="create-modules-section">
                <div className="flex items-center justify-between mb-2">
                  <Label>Módulos habilitados</Label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:text-indigo-700"
                      onClick={() => setAllCreateModules(true)}
                      data-testid="modules-select-all"
                    >Todos</button>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:text-slate-700"
                      onClick={() => setAllCreateModules(false)}
                      data-testid="modules-select-none"
                    >Ninguno</button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Seleccione los módulos visibles para esta empresa. Podrá editarlos después.
                </p>
                <div className="grid grid-cols-2 gap-2 border border-slate-200 rounded-lg p-3 bg-slate-50 max-h-64 overflow-y-auto">
                  {ALL_MODULES.map(m => {
                    const checked = createModules.includes(m.key);
                    return (
                      <label
                        key={m.key}
                        className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-2 py-1 transition-colors"
                        data-testid={`module-checkbox-row-${m.key}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleCreateModule(m.key)}
                          data-testid={`module-checkbox-${m.key}`}
                        />
                        <span className={`text-sm ${checked ? 'text-slate-800' : 'text-slate-400'}`}>{m.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {createModules.length} de {ALL_MODULES.length} módulos seleccionados
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="save-empresa-btn">
              {editing ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar empresa</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar "{deleting?.nombre}"? Solo se puede eliminar si no tiene usuarios asignados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-empresa">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ========== Users Tab ==========
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'mechanic', empresa_id: '' });
  const [deleting, setDeleting] = useState(null);
  const [changingEmpresa, setChangingEmpresa] = useState(null);
  const [newEmpresaId, setNewEmpresaId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, empresasRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: headers() }),
        axios.get(`${API}/empresas`, { headers: headers() }),
      ]);
      setUsers(usersRes.data);
      setEmpresas(empresasRes.data);
    } catch { toast.error('Error al cargar datos'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.email || !form.name || !form.password) {
      return toast.error('Todos los campos son obligatorios');
    }
    if (!form.empresa_id) {
      return toast.error('Debe seleccionar una empresa');
    }
    try {
      await axios.post(`${API}/users`, form, { headers: headers() });
      toast.success('Usuario creado');
      setShowCreateDialog(false);
      setForm({ email: '', name: '', password: '', role: 'mechanic', empresa_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deleting.id}`, { headers: headers() });
      toast.success('Usuario eliminado');
      setDeleting(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleChangeEmpresa = async () => {
    if (!newEmpresaId) return toast.error('Seleccione una empresa');
    try {
      await axios.put(`${API}/users/${changingEmpresa.id}/empresa`, { empresa_id: newEmpresaId }, { headers: headers() });
      toast.success('Empresa cambiada');
      setChangingEmpresa(null);
      setNewEmpresaId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  // Group users by empresa
  const grouped = {};
  for (const u of users) {
    const eid = u.empresa_id || 'sin-empresa';
    if (!grouped[eid]) {
      const emp = empresas.find(e => e.id === eid);
      grouped[eid] = { nombre: emp?.nombre || 'Sin empresa', users: [] };
    }
    grouped[eid].users.push(u);
  }

  if (loading) return <p className="text-slate-500">Cargando usuarios...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-700">Usuarios por empresa</h3>
        <Button
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-user-btn"
        >
          <UserPlus size={16} className="mr-1" /> Nuevo Usuario
        </Button>
      </div>

      <div className="space-y-4">
        {Object.entries(grouped).map(([eid, group]) => (
          <div key={eid} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <Building2 size={15} className="text-indigo-500" />
              <span className="font-medium text-slate-700 text-sm">{group.nombre}</span>
              <span className="text-xs text-slate-400 ml-1">({group.users.length})</span>
            </div>
            <div className="divide-y divide-slate-100">
              {group.users.map(u => (
                <div key={u.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`user-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 text-sm">{u.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        u.role === 'admin' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : u.role_name || 'Mecánico'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={() => { setChangingEmpresa(u); setNewEmpresaId(u.empresa_id || ''); }}
                      className="p-1.5 hover:bg-slate-100 rounded text-xs text-indigo-600"
                      title="Cambiar empresa"
                      data-testid={`change-empresa-${u.id}`}
                    >
                      <Building2 size={14} />
                    </button>
                    <button
                      onClick={() => setDeleting(u)}
                      className="p-1.5 hover:bg-red-50 rounded"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nombre</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo" data-testid="new-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@ejemplo.com" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="new-user-password" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mechanic">Mecánico</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Empresa</Label>
              <Select value={form.empresa_id} onValueChange={v => setForm({ ...form, empresa_id: v })}>
                <SelectTrigger data-testid="new-user-empresa">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} data-testid="save-user-btn">Crear Usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Empresa Dialog */}
      <Dialog open={!!changingEmpresa} onOpenChange={() => setChangingEmpresa(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar empresa de {changingEmpresa?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label>Nueva empresa</Label>
            <Select value={newEmpresaId} onValueChange={setNewEmpresaId}>
              <SelectTrigger data-testid="change-empresa-select">
                <SelectValue placeholder="Seleccionar empresa" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangingEmpresa(null)}>Cancelar</Button>
            <Button onClick={handleChangeEmpresa} data-testid="confirm-change-empresa">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar a "{deleting?.name}" ({deleting?.email})?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ========== Pending Users Tab ==========
function PendingUsersTab() {
  const [pending, setPending] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const fetchPending = useCallback(async () => {
    try {
      const [usersRes, empRes] = await Promise.all([
        axios.get(`${API}/users/pending`, { headers: headers() }),
        axios.get(`${API}/empresas`, { headers: headers() }),
      ]);
      setPending(usersRes.data);
      setEmpresas(empRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // Fetch roles when empresa is selected
  useEffect(() => {
    if (!selectedEmpresa) { setRoles([]); setSelectedRole(''); return; }
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${API}/roles?empresa_id=${selectedEmpresa}`, { headers: headers() });
        setRoles(res.data);
      } catch { setRoles([]); }
    };
    fetchRoles();
  }, [selectedEmpresa]);

  const handleAssign = async () => {
    if (!assigning || !selectedEmpresa) return toast.error('Selecciona una empresa');
    try {
      await axios.put(`${API}/users/${assigning.id}/empresa`, { empresa_id: selectedEmpresa }, { headers: headers() });
      if (selectedRole) {
        await axios.put(`${API}/users/${assigning.id}/assign-role?role_id=${selectedRole}`, {}, { headers: headers() });
      }
      toast.success(`${assigning.email} activado correctamente`);
      setAssigning(null);
      setSelectedEmpresa('');
      setSelectedRole('');
      setRoles([]);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-500">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="pending-users-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-700">Usuarios pendientes de activación</h3>
        <span className="text-xs text-slate-400">{pending.length} pendiente{pending.length !== 1 ? 's' : ''}</span>
      </div>

      {pending.length === 0 ? (
        <div className="py-8 text-center">
          <Clock size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">No hay usuarios pendientes</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pending.map(u => (
            <div key={u.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200" data-testid={`pending-user-${u.id}`}>
              <div className="min-w-0">
                <p className="font-medium text-slate-800 truncate">{u.email}</p>
                <p className="text-xs text-slate-500">{u.name || 'Sin nombre'} — <span className="text-amber-600 font-medium">Pendiente</span></p>
              </div>
              <Button
                size="sm"
                onClick={() => { setAssigning(u); setSelectedEmpresa(''); setSelectedRole(''); }}
                className="flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-btn)' }}
                data-testid={`assign-btn-${u.id}`}
              >
                <UserPlus size={14} className="mr-1" /> Activar
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={!!assigning} onOpenChange={() => setAssigning(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activar usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium">{assigning?.email}</p>
              <p className="text-xs text-slate-500">{assigning?.name || 'Sin nombre'}</p>
            </div>
            <div>
              <Label>Empresa *</Label>
              <Select value={selectedEmpresa} onValueChange={setSelectedEmpresa}>
                <SelectTrigger data-testid="assign-empresa-select">
                  <SelectValue placeholder="Seleccionar empresa..." />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rol {!selectedRole && <span className="text-xs text-slate-400">(por defecto: Mecánico)</span>}</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole} disabled={!selectedEmpresa}>
                <SelectTrigger data-testid="assign-role-select">
                  <SelectValue placeholder={selectedEmpresa ? 'Seleccionar rol...' : 'Selecciona empresa primero'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__super_admin__">Super Admin</SelectItem>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigning(null)}>Cancelar</Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedEmpresa}
              style={{ backgroundColor: 'var(--theme-btn)' }}
              data-testid="confirm-assign-btn"
            >
              Activar usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== Main Panel ==========
export default function SuperAdminPanel() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('empresas');

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Acceso restringido</h2>
          <p className="text-slate-500 mt-1">Solo el Super Administrador puede acceder a este panel.</p>
          <Button className="mt-4" onClick={() => navigate('/')}>Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="super-admin-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Shield size={22} className="text-purple-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Panel Super Admin</h1>
          <p className="text-sm text-slate-500">Gestión de empresas y usuarios</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-6 w-fit" data-testid="admin-tabs">
        <button
          onClick={() => setTab('empresas')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'empresas' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-empresas"
        >
          <Building2 size={16} /> Empresas
        </button>
        <button
          onClick={() => setTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'users' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-users"
        >
          <Users size={16} /> Usuarios
        </button>
        <button
          onClick={() => setTab('pending')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'pending' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-pending"
        >
          <Clock size={16} /> Pendientes
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        {tab === 'empresas' && <EmpresasTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'pending' && <PendingUsersTab />}
      </div>
    </div>
  );
}
